import { ChangePasswordConfirmation } from "@/login/ChangePasswordConfirmation.tsx";
import { ChangePasswordError } from "@/login/ChangePasswordError.tsx";
import { ExternalLogin } from "@/login/ExernalLogin.tsx";
import { ForceResetPassword } from "@/login/ForceReset.tsx";
import { ForgotPassword } from "@/login/ForgotPassword.tsx";
import { Login } from "@/login/Login.tsx";
import { LoginAdminAs } from "@/login/LoginAdminAs.tsx";
import { LoginError } from "@/login/LoginError.tsx";
import { Timeout } from "@/login/LoginTimeout.tsx";
import { TimeoutSSO } from "@/login/LoginTimeoutSso.tsx";
import { Logout } from "@/login/Logout.tsx";
import { OtpLogin } from "@/login/Otp.tsx";
import { PasswordChange } from "@/login/PasswordChange.tsx";
import { ResetPasswordError } from "@/login/ResetPasswordError.tsx";
import { ResetPasswordReceipt } from "@/login/ResetPasswordReceipt.tsx";
import { SelectDomain } from "@/login/SelectDomain.tsx";
import { Show as ViewsSelectOfficeShow } from "@/login/SelectOffice.tsx";
import { Troubleshoot } from "@/login/Troubleshoot.tsx";
import { UnblockLoginError } from "@/login/UnblockLoginError.tsx";
import { UnblockLoginReceipt } from "@/login/UnblockLoginReceipt.tsx";
import { UserAuthenticatedButDoesNotExistError } from "@/login/UserAuthenticatedButDoesNotExistError.tsx";
import {
  readAdminAsData,
  readDomainSelectionData,
  readLoginErrorData,
  readPasswordData,
  readUnknownUserData,
} from "@/login/loginData.ts";
import { refreshCsrfToken } from "@/shared/bootstrap/cvpartner.ts";
import { createRouter } from "@/shared/bootstrap/react.tsx";
import { prefetchCurrentUser } from "@/shared/queries/CurrentUser.tsx";
import { prefetchAllCountries } from "@/shared/queries/countries.ts";
import { prefetchLoggedInStatus } from "@/shared/queries/loggedInStatus.ts";
import type { CVPartnerReactRoute } from "@/shared/types.ts";
import { NotFound } from "@/shared/ui/errors/404.tsx";
import { RouteError } from "@/shared/ui/errors/500.tsx";
import { queryClientContext } from "@/shared/util/fetch.tsx";
import { getCvPartner, skipFetchingUser } from "@/shared/util/getCvPartner.ts";

type LoginErrorProps = Pick<
  React.ComponentProps<typeof LoginError>,
  "type" | "allowExtendedLogin" | "action"
>;

// Read inside the route rather than when the table is built: only the flow that
// rendered the page has its data. A reader throws `StalePayloadError` once the
// URL has moved on, and `RouteError` renders nothing while the page reloads.
const LoginErrorRoute: React.FC<LoginErrorProps> = (props) => {
  const { message, err_params, support_email } = readLoginErrorData();

  return (
    <LoginError
      {...props}
      message={message}
      errParams={err_params}
      supportEmail={support_email}
    />
  );
};

const AdminAsRoute: React.FC = () => (
  <LoginAdminAs users={readAdminAsData().users} />
);

const UnknownUserRoute: React.FC = () => {
  const { field, user_id, support_email, federation_group_members } =
    readUnknownUserData();

  return (
    <UserAuthenticatedButDoesNotExistError
      field={field}
      user_id={user_id}
      supportEmail={support_email}
      federationGroupMembers={federation_group_members}
    />
  );
};

const DomainSelectionRoute: React.FC = () => (
  <SelectDomain
    googleAppsDomains={readDomainSelectionData().google_apps_domains}
  />
);

const ResetPasswordErrorRoute: React.FC = () => {
  const data = readPasswordData();

  return (
    <ResetPasswordError
      user_id={data.user_id ?? undefined}
      submit_path={`/session/password/reset/${data.user_id}`}
      reset_password_code={data.reset_password_code ?? undefined}
    />
  );
};

const ResetPasswordRoute: React.FC = () => {
  const data = readPasswordData();

  return (
    <PasswordChange
      type="reset"
      user_id={data.user_id ?? undefined}
      submit_path={`/session/password/reset/${data.user_id}`}
      reset_password_code={data.reset_password_code ?? undefined}
    />
  );
};

const ChangePasswordRoute: React.FC = () => (
  <PasswordChange
    type="change"
    user_id={readPasswordData().user_id ?? undefined}
    submit_path="/session/password/change"
  />
);

const ChangePasswordConfirmationRoute: React.FC = () => (
  <ChangePasswordConfirmation
    user_id={readPasswordData().user_id ?? undefined}
    submit_path="/session/password/change"
  />
);

const ChangePasswordErrorRoute: React.FC = () => (
  <ChangePasswordError
    user_id={readPasswordData().user_id ?? undefined}
    submit_path="/session/password/change"
  />
);

export class LoginRouter implements CVPartnerReactRoute {
  render() {
    const router = createRouter(
      [
        {
          children: [
            {
              path: "login",
              children: [
                {
                  index: true,
                  element: <Login type="normal" />,
                },
                {
                  path: "admin",
                  children: [
                    {
                      index: true,
                      element: (
                        <Login
                          action="/session/admin"
                          allowExtendedLogin={false}
                          type="support"
                        />
                      ),
                    },
                    {
                      path: "error",
                      element: (
                        <LoginErrorRoute
                          action="/session/admin"
                          allowExtendedLogin={false}
                          type="support"
                        />
                      ),
                    },
                  ],
                },
                {
                  path: "otp",
                  element: <OtpLogin />,
                },
                {
                  path: "as",
                  element: <AdminAsRoute />,
                },
                {
                  path: "external",
                  children: [
                    {
                      index: true,
                      element: <ExternalLogin />,
                    },
                    {
                      path: "error",
                      element: (
                        <LoginErrorRoute
                          action="/session/external"
                          type="external"
                        />
                      ),
                    },
                  ],
                },
                {
                  path: "troubleshoot",
                  element: <Troubleshoot />,
                },
                {
                  path: "timeout",
                  element: <Timeout />,
                },
                {
                  path: "timeout_sso",
                  element: <TimeoutSSO />,
                },
                {
                  path: "signed_out",
                  element: <Logout />,
                },
                {
                  path: "error",
                  element: <LoginErrorRoute type="normal" />,
                },
                {
                  path: "user_authenticated_but_does_not_exist_error",
                  element: <UnknownUserRoute />,
                },
                {
                  path: "select/domain",
                  element: <DomainSelectionRoute />,
                },
              ],
            },
            {
              path: "resetpassword",
              children: [
                {
                  path: "receipt",
                  element: <ResetPasswordReceipt />,
                },
                {
                  path: "error",
                  element: <ResetPasswordErrorRoute />,
                },
                {
                  path: ":user_id",
                  element: <ResetPasswordRoute />,
                },
              ],
            },
            {
              path: "change_password",
              children: [
                {
                  index: true,
                  element: <ChangePasswordRoute />,
                },
                {
                  path: "confirmation",
                  element: <ChangePasswordConfirmationRoute />,
                },
                {
                  path: "error",
                  element: <ChangePasswordErrorRoute />,
                },
              ],
            },
            {
              path: "unblock",
              children: [
                {
                  path: "receipt",
                  element: <UnblockLoginReceipt />,
                },
                {
                  path: "error",
                  element: <UnblockLoginError />,
                },
              ],
            },
            {
              path: "forgotpassword",
              element: <ForgotPassword />,
            },
            {
              path: "force_reset_password",
              element: <ForceResetPassword />,
            },
            {
              path: "select/office",
              element: <ViewsSelectOfficeShow />,
              loader: async () => {
                await Promise.all([
                  prefetchCurrentUser(),
                  prefetchAllCountries(),
                ]);

                return null;
              },
            },
            {
              path: "*",
              element: <NotFound />,
            },
          ],
          loader: async ({ request, context }) => {
            const queryClient = context.get(queryClientContext);
            await refreshCsrfToken(queryClient);

            if (
              getCvPartner().isLoggedIn &&
              !skipFetchingUser(new URL(request.url).pathname)
            ) {
              await prefetchCurrentUser();
            } else {
              await prefetchLoggedInStatus();
            }

            return null;
          },
          errorElement: <RouteError />,
        },
      ],
      true,
    );

    return { router, selector: "container" };
  }
}
